import { SoundOutlined } from '@ant-design/icons';
import {
  Badge,
  Button,
  Collapse,
  message,
  Modal,
  Pagination,
  Spin,
} from 'antd';
import { useState } from 'react';

import {
  useGetLatestReleaseNoteQuery,
  useGetUnreadReleaseNotesQuery,
  useLazyGetReleaseNotesQuery,
  useMarkAllReleaseNotesReadMutation,
  useMarkReleaseNoteReadMutation,
} from '../../../services/releaseNotesApi';
import { ReleaseNote } from '../../../types';
import { ReleaseNoteView } from './ReleaseNoteView';
import {
  ButtonReleaseNotes,
  HistoryLoading,
  PaginationRow,
  ReleaseHistoryContainer,
} from './styles';

const { Panel } = Collapse;

const HISTORY_PAGE_SIZE = 10;

export function ReleaseNotes() {
  const [visible, setVisible] = useState(false);
  const [history, setHistory] = useState<ReleaseNote[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loaded, setLoaded] = useState(false);

  const { data: unread } = useGetUnreadReleaseNotesQuery();
  const { data: latest } = useGetLatestReleaseNoteQuery();
  const [getReleaseNotes, { isFetching }] = useLazyGetReleaseNotesQuery();
  const [markRead] = useMarkReleaseNoteReadMutation();
  const [markAllRead] = useMarkAllReleaseNotesReadMutation();

  const unreadCount = unread?.count || 0;

  async function loadHistory(nextPage: number) {
    try {
      const response = await getReleaseNotes(nextPage).unwrap();
      setHistory(response.results);
      setTotal(response.count);
      setPage(nextPage);
      setLoaded(true);
    } catch {
      message.error(
        'Não foi possível carregar o histórico de versões. Tente novamente.'
      );
    }
  }

  function handleToggleHistory(keys: string | string[]) {
    if (keys.length > 0 && !loaded) {
      loadHistory(1);
    }
  }

  function handleUnderstood() {
    if (latest) {
      markRead(latest.id);
    }
    setVisible(false);
  }

  function handleMarkAll() {
    markAllRead();
  }

  return (
    <>
      <ButtonReleaseNotes onClick={() => setVisible(true)} title="Novidades">
        <Badge count={unreadCount} size="small" offset={[-4, 2]}>
          <SoundOutlined style={{ fontSize: '20px' }} />
        </Badge>
      </ButtonReleaseNotes>

      <Modal
        title={latest ? `Novidades — PMIS ${latest.version}` : 'Novidades'}
        visible={visible}
        onCancel={() => setVisible(false)}
        footer={[
          <Button
            key="all"
            type="link"
            onClick={handleMarkAll}
            disabled={unreadCount === 0}
          >
            Marcar todas como lidas
          </Button>,
          <Button key="close" type="primary" onClick={handleUnderstood}>
            Entendi
          </Button>,
        ]}
        width={740}
        bodyStyle={{
          maxHeight: 'calc(100vh - 240px)',
          overflowY: 'auto',
          paddingRight: 28,
        }}
      >
        {latest ? (
          <ReleaseNoteView releaseNote={latest} />
        ) : (
          <span>Nenhuma novidade publicada ainda.</span>
        )}

        <ReleaseHistoryContainer>
          <Collapse ghost onChange={handleToggleHistory}>
            <Panel header="Versões anteriores" key="history">
              {isFetching ? (
                <HistoryLoading>
                  <Spin />
                </HistoryLoading>
              ) : (
                <Collapse accordion ghost>
                  {history
                    .filter((note) => note.id !== latest?.id)
                    .map((note) => (
                      <Panel
                        header={`${note.version} — ${note.title}`}
                        key={String(note.id)}
                      >
                        <ReleaseNoteView releaseNote={note} />
                      </Panel>
                    ))}
                </Collapse>
              )}
              {total > HISTORY_PAGE_SIZE && (
                <PaginationRow>
                  <Pagination
                    current={page}
                    total={total}
                    pageSize={HISTORY_PAGE_SIZE}
                    onChange={loadHistory}
                    showSizeChanger={false}
                    size="small"
                  />
                </PaginationRow>
              )}
            </Panel>
          </Collapse>
        </ReleaseHistoryContainer>
      </Modal>
    </>
  );
}
